import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';

import Description from '../../components/Description/Description';
import Group from '../../components/Group/Group';
import IconWithText from '../../components/IconWithText/IconWithText';
import MonetaryField from '../../components/MonetaryField/MonetaryField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import ShowDetailsModal from '../../components/ShowDetailsModal/ShowDetailsModal';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import { ValidationResultType } from '../../hooks/useFieldValidation';
import usePagesConfig from '../../hooks/usePagesConfig';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';

const IncomePageSE = () => {
    useComponentTracker('FinancePage');

    const translate = useTranslation();
    const { totalSections, totalAboutYouPages } = usePagesConfig();
    const { currentPage } = usePagesContext();

    const validationErrors = useValidation([
        'applicantMonthlyGrossIncome',
        'applicantMortgageMonthlyCost',
        'applicantOtherLoans',
    ]);

    const defaultPagesAction = useDefaultPagesAction();
    const onAction = async (action: WizardPageAction) => {
        if (action === 'next' && validationErrors.length) {
            return false;
        }

        return defaultPagesAction(action);
    };

    const employmentType = useAppSelector(selectFieldValue)('applicantEmploymentType');

    const minValueValidation = async (value?: string): Promise<ValidationResultType> => {
        const isValid = (value ?? 0) > '0';

        return {
            valid: isValid,
            errorMessageKey: isValid ? undefined : translate('field.applicant.min-value.error'),
        };
    };

    return (
        <>
            <Title text={translate('page.finance.title')} />
            <Description text={translate('page.finance.description')} />
            <ShowDetailsModal
                label={translate('dialog.know-more.label')}
                dialogTitle={translate('dialog.know-more.label')}
                dialogContent={translate('page.finance.dialog.content')}
            />
            <Group>
                <MonetaryField
                    name="applicantMonthlyGrossIncome"
                    label={translate('field.applicant-monthly-gross-income.label')}
                    customValidation={minValueValidation}
                />
                <MonetaryField
                    name="applicantMortgageMonthlyCost"
                    label={translate('field.applicant-mortage-monthly-cost.label')}
                    customValidation={minValueValidation}
                />
                <MonetaryField
                    name="applicantOtherLoans"
                    label={translate('field.applicant-other-loans-monthly-cost.label')}
                    customValidation={minValueValidation}
                />
            </Group>
            {employmentType === 'PENSIONER' ? (
                <IconWithText icon={faCircleExclamation} text="page.finance.body-text" iconSize="sm" />
            ) : null}

            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default IncomePageSE;
